import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from 'react';
import { Copy, X } from 'lucide-react';
import type { Reconciliation } from '@molt/shared';
import { Button, Dialog, EmptyState, IconButton, Notice, TextField } from '@/components';
import type {
  DestinationBoardOption,
  DestinationListOption,
} from '@/lib/cross-board-move';
import {
  cardMoveNeighbours,
  hasReconciliationConsequences,
  type CardMovePlacement,
} from '@/lib/cross-board-move';
import { cardDisplayTitle } from '@/lib/card-title';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import { MoveReconciliationReport } from './MoveReconciliationReport';

export interface CardCopyDialogSubmit {
  readonly boardId: string;
  readonly listId: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
  readonly title: string;
  readonly keepChecklists: boolean;
  readonly keepLabels: boolean;
  readonly keepMembers: boolean;
  readonly keepAttachments: boolean;
  readonly keepDates: boolean;
}

export interface CardCopyDialogProps {
  readonly open: boolean;
  /**
   * Focused on close instead of the element that opened the dialog, for an
   * opener that no longer exists by then: the card back opens this from an
   * item of its "⋯" Actions menu, and hands focus back to the menu trigger.
   */
  readonly returnFocus?: RefObject<HTMLElement | null> | undefined;
  readonly subjectTitle: string;
  readonly currentBoardId: string;
  readonly currentListId: string;
  readonly currentCardId: string;
  readonly boards: readonly DestinationBoardOption[];
  readonly listsByBoardId: ReadonlyMap<string, readonly DestinationListOption[]>;
  readonly cardsByListId: ReadonlyMap<string, readonly { id: string; title: string }[]>;
  readonly loading?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly result?: Reconciliation | null | undefined;
  readonly onCancel: () => void;
  readonly onCopy: (input: CardCopyDialogSubmit) => void;
}

export function CardCopyDialog({
  open,
  subjectTitle,
  currentBoardId,
  currentListId,
  currentCardId,
  boards,
  listsByBoardId,
  cardsByListId,
  loading,
  busy,
  result,
  onCancel,
  onCopy,
  returnFocus,
}: CardCopyDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const titleField = useRef<HTMLInputElement | null>(null);
  const boardSelectId = useId();
  const listSelectId = useId();
  const positionId = useId();
  /* ⚠ THE LEAF, not an eighth author of `Untitled card`. `cardDisplayTitle`
     applies exactly the rule this line already applied. */
  const cleanTitle = cardDisplayTitle(t, subjectTitle);

  const [title, setTitle] = useState(cleanTitle);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [selectedListId, setSelectedListId] = useState('');
  const [placement, setPlacement] = useState<CardMovePlacement>('bottom');
  const [keepChecklists, setKeepChecklists] = useState(true);
  const [keepLabels, setKeepLabels] = useState(true);
  const [keepMembers, setKeepMembers] = useState(true);
  const [keepAttachments, setKeepAttachments] = useState(true);
  const [keepDates, setKeepDates] = useState(true);

  const defaultBoardId = useMemo(() => {
    if (boards.some((board) => board.id === currentBoardId)) return currentBoardId;
    return boards[0]?.id ?? '';
  }, [boards, currentBoardId]);

  const selectedBoard =
    boards.find((board) => board.id === selectedBoardId) ??
    boards.find((board) => board.id === defaultBoardId) ??
    null;
  const availableLists =
    selectedBoard === null ? [] : (listsByBoardId.get(selectedBoard.id) ?? []);
  const defaultListId = useMemo(() => {
    if (
      selectedBoard?.id === currentBoardId &&
      availableLists.some((list) => list.id === currentListId)
    ) {
      return currentListId;
    }
    return availableLists[0]?.id ?? '';
  }, [availableLists, currentBoardId, currentListId, selectedBoard?.id]);
  const effectiveListId = availableLists.some((list) => list.id === selectedListId)
    ? selectedListId
    : defaultListId;
  const positionCards = cardsByListId.get(effectiveListId) ?? [];
  const placementChoices = useMemo(
    () => copyPlacementChoices(t, positionCards),
    [positionCards, t],
  );

  useEffect(() => {
    if (!open) return;
    setTitle(cleanTitle);
    setSelectedBoardId(defaultBoardId);
    setSelectedListId('');
    setPlacement('bottom');
    setKeepChecklists(true);
    setKeepLabels(true);
    setKeepMembers(true);
    setKeepAttachments(true);
    setKeepDates(true);
  }, [cleanTitle, defaultBoardId, open]);

  useEffect(() => {
    if (!open) return;
    setPlacement(defaultCopyPlacement(positionCards, currentCardId));
  }, [currentCardId, effectiveListId, open, positionCards]);

  if (!open) return null;

  const titleError = title.trim().length === 0 ? t('copyMove.titleRequired') : null;
  const hasNoDestinationList = selectedBoard !== null && availableLists.length === 0;
  const confirmDisabled =
    busy === true ||
    loading === true ||
    selectedBoard === null ||
    effectiveListId === '' ||
    titleError !== null;
  const disabledReason =
    loading === true
      ? t('copyMove.loadingBoards')
      : selectedBoard === null
        ? t('copyMove.chooseBoardFirst')
        : hasNoDestinationList
          ? t('copyMove.card.needsList')
          : (titleError ?? undefined);
  const disabledReasonProps = disabledReason ? { disabledReason } : {};
  const busyProps = busy === undefined ? {} : { busy };

  function submitCopy() {
    if (selectedBoard === null || effectiveListId === '' || title.trim().length === 0) {
      return;
    }
    const neighbours = cardMoveNeighbours(positionCards, placement);
    onCopy({
      boardId: selectedBoard.id,
      listId: effectiveListId,
      previousCardId: neighbours.previousCardId,
      nextCardId: neighbours.nextCardId,
      title: title.trim(),
      keepChecklists,
      keepLabels,
      keepMembers,
      keepAttachments,
      keepDates,
    });
  }

  return (
    <Dialog
      {...(returnFocus ? { returnFocus } : {})}
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={t('copyMove.card.title', { title: cleanTitle })}
      description={t('copyMove.card.description')}
      initialFocus={titleField}
      closeControl={
        <IconButton label={t('copyMove.closeCopy')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {result ? t('copyMove.done') : t('copyMove.cancel')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            iconStart={<Copy aria-hidden="true" className="size-4" />}
            busyLabel={t('copyMove.card.busy')}
            disabled={confirmDisabled}
            onClick={submitCopy}
            {...busyProps}
            {...disabledReasonProps}
          >
            {t('copyMove.card.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {result !== null && result !== undefined ? (
          hasReconciliationConsequences(result) ? (
            <MoveReconciliationReport
              reconciliation={result}
              summary={t('copyMove.card.resultSummary')}
              tone="result"
            />
          ) : (
            <Notice
              tone="info"
              title={t('copyMove.card.copiedTitle')}
              body={t('copyMove.card.copiedBody')}
            />
          )
        ) : null}

        <TextField
          ref={titleField}
          label={t('copyMove.copyTitleLabel')}
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          {...(titleError === null ? {} : { error: titleError })}
        />

        {boards.length === 0 ? (
          <EmptyState
            tone="neutral"
            headingLevel={3}
            title={t('copyMove.noBoards.title')}
            body={t('copyMove.noBoards.body')}
          />
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <label htmlFor={boardSelectId} className="text-sm font-medium text-ink">
                {t('copyMove.destinationBoardLabel')}
              </label>
              <select
                id={boardSelectId}
                value={selectedBoard?.id ?? ''}
                onChange={(event) => {
                  setSelectedBoardId(event.currentTarget.value);
                  setSelectedListId('');
                  setPlacement('bottom');
                }}
                className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent"
              >
                {boards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {t('copyMove.boardOption', {
                      board: board.title,
                      workspace: board.workspaceName,
                    })}
                  </option>
                ))}
              </select>
            </div>

            {hasNoDestinationList ? (
              <EmptyState
                tone="neutral"
                headingLevel={3}
                title={t('copyMove.noOpenLists')}
                body={t('copyMove.card.noListsBody', {
                  board: selectedBoard?.title ?? t('copyMove.card.thatBoard'),
                })}
              />
            ) : (
              <div className="flex flex-col gap-1">
                <label htmlFor={listSelectId} className="text-sm font-medium text-ink">
                  {t('copyMove.destinationListLabel')}
                </label>
                <select
                  id={listSelectId}
                  value={effectiveListId}
                  onChange={(event) => {
                    setSelectedListId(event.currentTarget.value);
                    setPlacement('bottom');
                  }}
                  className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent"
                >
                  {availableLists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!hasNoDestinationList ? (
              <fieldset className="flex flex-col gap-2">
                <legend id={positionId} className="text-sm font-medium text-ink">
                  {t('copyMove.positionLabel')}
                </legend>
                <div className="flex max-h-40 flex-col gap-1 overflow-y-auto pr-1">
                  {placementChoices.map((choice) => (
                    <label
                      key={choice.value}
                      className="flex items-center gap-2 rounded-card px-2 py-1 text-sm text-ink hover:bg-surface-raised"
                    >
                      <input
                        type="radio"
                        name="card-copy-placement"
                        value={choice.value}
                        checked={placement === choice.value}
                        onChange={() => setPlacement(choice.value)}
                      />
                      <span>{choice.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}
          </>
        )}

        <fieldset className="grid gap-2 sm:grid-cols-2">
          <legend className="sr-only">{t('copyMove.card.keepLegend')}</legend>
          <KeepChoice
            label={t('copyMove.card.keepChecklists')}
            checked={keepChecklists}
            onChange={setKeepChecklists}
          />
          <KeepChoice
            label={t('copyMove.card.keepLabels')}
            checked={keepLabels}
            onChange={setKeepLabels}
          />
          <KeepChoice
            label={t('copyMove.card.keepMembers')}
            checked={keepMembers}
            onChange={setKeepMembers}
          />
          <KeepChoice
            label={t('copyMove.card.keepAttachments')}
            checked={keepAttachments}
            onChange={setKeepAttachments}
          />
          <KeepChoice
            label={t('copyMove.card.keepDates')}
            checked={keepDates}
            onChange={setKeepDates}
          />
        </fieldset>
      </div>
    </Dialog>
  );
}

function KeepChoice({
  label,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 rounded-card border border-border bg-surface-raised px-3 py-2 text-sm text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

/**
 * ⚠ `t` IS THE REQUIRED FIRST PARAMETER, because a module-level helper cannot
 * call the hook and a module-level constant cannot call `t` at all. The
 * `WorkspaceSwitcher` / `BOARD_ROLE_LABELS` shape, one step further: the caller
 * threads `t` in and every label is resolved at render.
 *
 * ⚠ The `After …` fallback reaches `cardDisplayTitle` rather than re-authoring
 * `Untitled card`; the leaf applies byte-for-byte the rule this helper did.
 */
function copyPlacementChoices(
  t: InterfaceLanguageContextValue['t'],
  cards: readonly { id: string; title: string }[],
): readonly { value: CardMovePlacement; label: string }[] {
  return [
    { value: 'top', label: t('copyMove.position.topOfList') },
    { value: 'bottom', label: t('copyMove.position.bottomOfList') },
    ...cards.map((card) => ({
      value: `after:${card.id}` as const,
      label: t('copyMove.position.afterCard', {
        card: cardDisplayTitle(t, card.title),
      }),
    })),
  ];
}

function defaultCopyPlacement(
  cards: readonly { id: string }[],
  currentCardId: string,
): CardMovePlacement {
  return cards.some((card) => card.id === currentCardId)
    ? (`after:${currentCardId}` as const)
    : 'bottom';
}
